/**
 * @license
 * Copyright 2018 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @file Support for rendering point annotations.
 */

import { AnnotationType, Point } from 'neuroglancer/annotation';
import { AnnotationRenderContext, AnnotationRenderHelper, AnnotationShaderGetter, registerAnnotationTypeRenderHandler } from 'neuroglancer/annotation/type_handler';
import { defineCircleShader, drawCircles, initializeCircleShader } from 'neuroglancer/webgl/circles';
import { defineLineShader, drawLines, initializeLineShader } from 'neuroglancer/webgl/lines';
import { ShaderBuilder, ShaderProgram } from 'neuroglancer/webgl/shader';
import { defineVectorArrayVertexShaderInput } from 'neuroglancer/webgl/shader_lib';
import { defineVertexId, VertexIdHelper } from 'neuroglancer/webgl/vertex_id';
import { allToolsButton, viewer } from 'src/main';
import {SegmentColorShaderManager, SegmentStatedColorShaderManager} from 'neuroglancer/segment_color';
import {GPUHashTable, HashSetShaderManager} from 'neuroglancer/gpu_hash/shader';
import {HashMapUint64} from 'neuroglancer/gpu_hash/hash_table';
import {Uint64Set} from "neuroglancer/uint64_set";
import { find_exist_property } from '../util/comm_find';

interface DrawOptions {
  hasMapColor?: boolean, // 是否有离散颜色映射
  isUMAP?:boolean, // 是否是UMAP
  geneproperty?: any,
  isRect?: boolean, // 是否是矩形绘制（代谢组）
  extraDim: number
}
class RenderHelper extends AnnotationRenderHelper {
  visibleSegments: any
  gpuHashTable: any
  colorHash: any
  segmentStatedColors: any
  visibleCellGpuHashTable: any;
  protected segmentStatedColorShaderManager =
    new SegmentStatedColorShaderManager('segmentStatedColor');
  protected segmentColorShaderManager = new SegmentColorShaderManager('segmentColorHash');
  private gpuSegmentStatedColorHashTable: GPUHashTable<HashMapUint64>|undefined;
  private hashTableManager = new HashSetShaderManager('visibleSegments');
  private visibleCellHashTableManager = new HashSetShaderManager('visibleCell');
  private defineShaderCommon(builder: ShaderBuilder) {
    const {rank} = this;
    // Position of point in model coordinates.
    defineVectorArrayVertexShaderInput(
        builder, 'float', WebGL2RenderingContext.FLOAT, /*normalized=*/ false, 'VertexPosition',
        rank);
    builder.addVarying('highp vec4', 'vBorderColor');
    builder.addAttribute('highp float', 'aPointSize');
    builder.addVarying('highp uint', 'vXPosition', 'flat');
    builder.addVarying('highp uint', 'vYPosition', 'flat');
    builder.addVarying('highp uint', 'vClusterXPosition', 'flat');
    builder.addVarying('highp uint', 'vClusterYPosition', 'flat');
    builder.addUniform('highp uint', 'uShowAllCell');
    this.hashTableManager.defineShader(builder);
    this.visibleCellHashTableManager.defineShader(builder);
    this.segmentColorShaderManager.defineShader(builder);
    this.segmentStatedColorShaderManager.defineShader(builder);
    builder.addVertexCode(`
float ng_markerDiameter;
float ng_markerBorderWidth;
float ng_filterProper;
float pointFactor;
float opacityFactor;
void setPointMarkerSize(float size) {
  ng_markerDiameter = size;
}
void setPointMarkerFactor(float size) {
  pointFactor = size;
}
void setPointMarkerOpacityFactor(float a){
  opacityFactor = a;
}
void setPointMarkerBorderWidth(float size) {
  ng_markerBorderWidth = size;
}
void setPointMarkerColor(vec4 color) {
  vColor = color;
}
void setPointMarkerBorderColor(vec4 color) {
  vBorderColor = color;
}
`);
    builder.addVertexMain(`
ng_markerDiameter = aPointSize;
ng_markerBorderWidth = -1.0;
pointFactor = 1.0;
opacityFactor = 1.0;
vBorderColor = vec4(0.0, 0.0, 0.0, 1.0);
float modelPosition[${rank}] = getVertexPosition0();
float clipCoefficient = getSubspaceClipCoefficient(modelPosition);
if (clipCoefficient == 0.0) {
  gl_Position = vec4(2.0, 0.0, 0.0, 1.0);
  return;
}
${this.invokeUserMain}
vColor.a *= clipCoefficient * opacityFactor;
vBorderColor.a *= clipCoefficient;
${this.setPartIndex(builder)};
`);
  }

  private shaderGetter3d =
      this.getDependentShader('annotation/point:3d', (builder: ShaderBuilder) => {
        defineVertexId(builder);
        defineCircleShader(builder, /*crossSectionFade=*/ this.targetIsSliceView);
        this.defineShaderCommon(builder);
        builder.addVertexMain(`
emitCircle(uModelViewProjection *
           vec4(projectModelVectorToSubspace(modelPosition), 1.0), ng_markerDiameter*pointFactor, ng_markerBorderWidth);
`);
        builder.setFragmentMain(`
vec4 color = getCircleColor(vColor, vBorderColor);
emitAnnotation(color);
`);
      });
  // 聚类shader
  private makeShaderGetter2dCluster = (options: DrawOptions) => {
    const {extraDim, hasMapColor, isUMAP, geneproperty, isRect} = options
    return this.getDependentShader(`annotation/point:2d:${extraDim}:${hasMapColor}${isUMAP}`, (builder: ShaderBuilder) => {
      defineVertexId(builder);
      defineLineShader(builder, /*rounded=*/ true);
      let defin_position = isUMAP && find_exist_property(geneproperty, 'category_x') ? `
      vClusterXPosition = uint(prop_category_x());
      vClusterYPosition = uint(prop_category_y());` : `vXPosition = uint(modelPosition[0]);
      vYPosition = uint(modelPosition[1]);`;
      let defin_shader = isUMAP && find_exist_property(geneproperty, 'category_y') ? `
      uint64_t xInfo;
      xInfo.value[0] = vClusterXPosition;
      xInfo.value[1] = vClusterYPosition;
      bool hasCell = uShowAllCell != 1u ? true : ${this.visibleCellHashTableManager.hasFunctionName}(xInfo);
      if (!hasCell) {
        discard;
      }`:`uint64_t xInfo;
      xInfo.value[0] = vXPosition;
      xInfo.value[1] = vYPosition;
      bool hasCell = uShowAllCell != 1u ? true : ${this.visibleCellHashTableManager.hasFunctionName}(xInfo);
      if (!hasCell) {
        discard;
      }`;
      this.defineShaderCommon(builder);
      builder.addVarying('highp vec4', 'vPropertyColor', 'flat');
      builder.addUniform('highp uint', 'uShowAllSegments');
      builder.addUniform('highp vec2', 'uPointScaleRange');
      builder.addVertexCode(`
      // 指定过滤的属性
      void discardByIntProperty(highp uint property) {
        ng_filterProper = float(property);
      }
      void discardByFloatProperty(float property) {
        ng_filterProper = float(property);
      }`)
      builder.addVertexMain(`
vec3 subspacePositionA = projectModelVectorToSubspace(modelPosition);
vec3 subspacePositionB = subspacePositionA;
vec4 baseProjection = uModelViewProjection * vec4(subspacePositionA, 1.0);
vec4 zCoeffs = uModelViewProjection[${extraDim}];
float minZ = 1e30;
float maxZ = -1e30;
for (int i = 0; i < 3; ++i) {
// Want: baseProjection[i] + z * zCoeffs[i] = -2.0 * (baseProjection.w - z * zCoeffs.w)
//  i.e. baseProjection[i] + 2.0 * baseProjection.w < -z * (2.0 * zCoeffs.w + zCoeffs[i])
//  i.e. baseProjection[i] + 2.0 * baseProjection.w < -z * k1
float k1 = 2.0 * zCoeffs.w + zCoeffs[i];
float q1 = -(baseProjection[i] + 2.0 * baseProjection.w) / k1;
if (k1 != 0.0) {
  minZ = min(minZ, q1);
  maxZ = max(maxZ, q1);
}
// Want: baseProjection[i] + z * zCoeffs[i] = 2.0 * (baseProjection.w + z * zCoeffs.w)
//  i.e. baseProjection[i] - 2.0 * baseProjection.w > z * (2.0 * zCoeffs.w - zCoeffs[i])
//  i.e. baseProjection[i] - 2.0 * baseProjection.w > z * k2
float k2 = 2.0 * zCoeffs.w - zCoeffs[i];
float q2 = (baseProjection[i] - 2.0 * baseProjection.w) / k2;
if (k2 != 0.0) {
  minZ = min(minZ, q2);
  maxZ = max(maxZ, q2);
}
}
if (minZ > maxZ) minZ = maxZ = 0.0;
subspacePositionA[${extraDim}] = minZ;
subspacePositionB[${extraDim}] = maxZ;
vPropertyColor = vec4(1.0, 0, float(ng_filterProper), 0);
emitLine(uModelViewProjection, subspacePositionA, subspacePositionB, ng_markerDiameter*pointFactor, ng_markerBorderWidth, uPointScaleRange);
${defin_position}
`);
      // 离散颜色
      let getMappedIdColor = `vec3 getMappedIdColor(uint64_t value) {
        `;
        // If the value has a mapped color, use it; otherwise, compute the color.
        if (hasMapColor) {
          getMappedIdColor += `
            vec3 rgb;
            if (${this.segmentStatedColorShaderManager.getFunctionName}(value, rgb)) {
              return rgb;
            } else {
              return segmentColorHash(value);
            }
          `;
      } else {
          getMappedIdColor += `  return segmentColorHash(value);
        `;
      }
      getMappedIdColor += `
      }
      `;
      builder.addFragmentCode(getMappedIdColor);

      builder.setFragmentMain(`
      // vec4 color = getRoundedLineColor(vColor, vBorderColor);
      vec4 color = ${isRect ? 'vColor': 'getRoundedLineColor(vColor, vBorderColor)'};

      uint64_t result;
      result.value[0] = uint(vPropertyColor.z);
      // result.value[0] = 2000u;
      result.value[1] = 0u;
      bool has = uShowAllSegments != 0u ? true : ${this.hashTableManager.hasFunctionName}(result);
      if (!has) {
        discard;
      }
      vec3 rgb = getMappedIdColor(result);

      if (vSelected == 1u) {
        emitAnnotation(vec4(mix(rgb.rgb, vec3(1.0,1.0,1.0), 0.75), vColor.a));
      }else{
        emitAnnotation(vec4(rgb, vColor.a));
      }
      ${defin_shader}
`);
    });
  }
   
  private makeShaderGetter2d = (options: DrawOptions) => {
    const {isRect = false, extraDim} = options
    return this.getDependentShader(`annotation/point:2d:${extraDim}`, (builder: ShaderBuilder) => {
      defineVertexId(builder);
      defineLineShader(builder, /*rounded=*/ true);
      this.defineShaderCommon(builder);
      builder.addUniform('highp vec3', 'uColorBarRGBList', 10);
      builder.addUniform('highp int', 'uColorBarMax');
      builder.addUniform('highp int', 'uColorBarMin');
      builder.addUniform('highp vec2', 'uPointScaleRange');
      builder.addVertexMain(`
            vColor.rgb = colormapCustom(int(prop_midCount()),uColorBarMin,uColorBarMax,uColorBarRGBList);
            `
      )
      builder.addVertexMain(`
  vec3 subspacePositionA = projectModelVectorToSubspace(modelPosition);
  vec3 subspacePositionB = subspacePositionA;
  vec4 baseProjection = uModelViewProjection * vec4(subspacePositionA, 1.0);
  vec4 zCoeffs = uModelViewProjection[${extraDim}];
  float minZ = 1e30;
  float maxZ = -1e30;
  for (int i = 0; i < 3; ++i) {
  // Want: baseProjection[i] + z * zCoeffs[i] = -2.0 * (baseProjection.w - z * zCoeffs.w)
  //  i.e. baseProjection[i] + 2.0 * baseProjection.w < -z * (2.0 * zCoeffs.w + zCoeffs[i])
  //  i.e. baseProjection[i] + 2.0 * baseProjection.w < -z * k1
  float k1 = 2.0 * zCoeffs.w + zCoeffs[i];
  float q1 = -(baseProjection[i] + 2.0 * baseProjection.w) / k1;
  if (k1 != 0.0) {
  minZ = min(minZ, q1);
  maxZ = max(maxZ, q1);
  }
  // Want: baseProjection[i] + z * zCoeffs[i] = 2.0 * (baseProjection.w + z * zCoeffs.w)
  //  i.e. baseProjection[i] - 2.0 * baseProjection.w > z * (2.0 * zCoeffs.w - zCoeffs[i])
  //  i.e. baseProjection[i] - 2.0 * baseProjection.w > z * k2
  float k2 = 2.0 * zCoeffs.w - zCoeffs[i];
  float q2 = (baseProjection[i] - 2.0 * baseProjection.w) / k2;
  if (k2 != 0.0) {
  minZ = min(minZ, q2);
  maxZ = max(maxZ, q2);
  }
  }
  if (minZ > maxZ) minZ = maxZ = 0.0;
  subspacePositionA[${extraDim}] = minZ;
  subspacePositionB[${extraDim}] = maxZ;
  emitLine(uModelViewProjection, subspacePositionA, subspacePositionB, ng_markerDiameter*pointFactor, ng_markerBorderWidth, uPointScaleRange);
  vXPosition = uint(modelPosition[0]);
  vYPosition = uint(modelPosition[1]);
  `);
  
      builder.setFragmentMain(`
      // vec4 color = getRoundedLineColor(vColor, vBorderColor);
      vec4 color = ${isRect ? 'vColor': 'getRoundedLineColor(vColor, vBorderColor)'};
      emitAnnotation(vec4(color.rgb, color.a * ${this.getCrossSectionFadeFactor()}));
  
      uint64_t xInfo;
      xInfo.value[0] = vXPosition;
      xInfo.value[1] = vYPosition;
      bool hasCell = uShowAllCell != 1u ? true : ${this.visibleCellHashTableManager.hasFunctionName}(xInfo);
      if (!hasCell) {
        discard;
      }
  `);
    });
  }
  

  // private shaderGetter2d = this.makeShaderGetter2d(2);
  // private shaderGetter2dHasMapColor = this.makeShaderGetter2dCluster(2, true);

  // private shaderGetter2dHasNoMapColor = this.makeShaderGetter2dCluster(2, false);

  // TODO(jbms): This rendering for the 1d case is not correct except for cross-section/orthographic
  // projection views where the "z" dimension is orthogonal to the single annotation chunk
  // dimension.
  // private shaderGetter1d = this.makeShaderGetter2d(1);

  private vertexIdHelper = this.registerDisposer(VertexIdHelper.get(this.gl));

  private crossSectionScale: number

  enable(
      shaderGetter: AnnotationShaderGetter, context: AnnotationRenderContext,
      callback: (shader: ShaderProgram) => void) {
    super.enable(shaderGetter, context, shader => {
      const binder = shader.vertexShaderInputBinders['VertexPosition'];
      binder.enable(1);
      this.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, context.buffer.buffer);
      binder.bind(this.geometryDataStride, context.bufferOffset);
      const {vertexIdHelper} = this;
      vertexIdHelper.enable();
      callback(shader);
      vertexIdHelper.disable();
      binder.disable();
    });
  }

  draw(context: AnnotationRenderContext) {
    // console.log(context)
    const {numChunkDisplayDims} = context.chunkDisplayTransform;
    const {isUmap, geneproperty, display_size} = context.renderContext;
    const isRect = display_size && Object.keys(display_size).length > 0 && (display_size.y / display_size.x !== 1) ? true : false;
    if (context.renderContext.drawSegmentContext) {
      const {segmentColorHash: {value: colorHash},  segmentStatedColors:{value: segmentStatedColors}, segmentationGroupState: {value: {visibleSegments: visibleSegments}}} = context.renderContext.drawSegmentContext
      this.gpuHashTable = this.registerDisposer(
        GPUHashTable.get(this.gl, visibleSegments.hashTable));
      this.colorHash =colorHash
      this.segmentStatedColors = segmentStatedColors
      this.visibleSegments = visibleSegments
    }
    viewer.proteinsFilter.cellPositionHashSet || (viewer.proteinsFilter.cellPositionHashSet = new Uint64Set());
    if(viewer.proteinsFilter.cellPositionHashSet){
      this.visibleCellGpuHashTable = GPUHashTable.get(this.gl, viewer.proteinsFilter.cellPositionHashSet.hashTable);
    }
    const hasMapColor = this.segmentStatedColors && this.segmentStatedColors.size !== 0
    // if (!this.crossSectionScale && viewer.initScale) this.crossSectionScale = viewer.initScale
    if (!this.crossSectionScale && viewer.homeCrossSectionScale) this.crossSectionScale = 1 / viewer.homeCrossSectionScale
    switch (numChunkDisplayDims) {
      case 3:
        this.enable(this.shaderGetter3d, context, shader => {
          const {gl} = shader;
          // let _url:any = '';
          // for(let i = 0; i < viewer.state.toJSON().layers.length; i++){
          //   if(viewer.state.toJSON().layers[i].type === 'annotation'){
          //     _url = viewer.state.toJSON().layers[i].source;
          //     break;
          //   }
          // } ;
          // console.log(_url)
          // let isBin = typeof _url === 'string' ? _url?.includes('bin') : Array.isArray(_url) ? _url[0].includes('bin') : _url.url.includes('bin');
          // let binSizes:any = typeof _url === 'string' ? _url?.split('/') : Array.isArray(_url) ? _url[0].split('/') : _url.url.split('/');
          // binSizes = isBin ? Number(binSizes[binSizes.length -1].substr(3)): 1.0;
          let pointsize = 1.0 / (isUmap ? this.crossSectionScale : viewer.crossSectionScale.value);
          gl.vertexAttrib1f(shader.attribute('aPointSize'),pointsize);
          initializeCircleShader(
              shader, context.renderContext.projectionParameters, {featherWidthInPixels: 1});
          drawCircles(shader.gl, 1, context.count);
        });
        break;
      case 2:
      case 1:
        const notCluster = !context.renderContext.isCluster;
        this.enable(
            numChunkDisplayDims === 2 ? (notCluster ? this.makeShaderGetter2d({extraDim: 2, isRect: isRect }) : this.makeShaderGetter2dCluster({extraDim: 2 ,hasMapColor: Boolean(hasMapColor), isUMAP: isUmap, geneproperty, isRect: isRect} )) : this.makeShaderGetter2d({extraDim: 1}), context,
            shader => {
              const {gl} = shader;
              const bin = viewer.binSize.value as string;
              const binSizes = bin.includes('bin') && bin!== 'cellbin' ? Number(bin.replace('bin','')): 1.0;
              let pointsize = isUmap ? 8 : 1.0 / (viewer.crossSectionScale.value) * binSizes;
              pointsize = Math.max(pointsize, 3.0);
              gl.vertexAttrib1f(shader.attribute('aPointSize'),pointsize);

              // 根据长度判断是否展示所有cell, 长度为0则展示所有
              if (viewer.proteinsFilter.cellPositionHashSet?.size) {
                gl.uniform1ui(shader.uniform('uShowAllCell'), 1);
              } else {
                gl.uniform1ui(shader.uniform('uShowAllCell'), 0);
              }
              if (this.visibleCellGpuHashTable) {
                this.visibleCellGpuHashTable?.dispose();
                if(viewer.proteinsFilter.cellPositionHashSet){
                  this.visibleCellGpuHashTable = GPUHashTable.get(this.gl, viewer.proteinsFilter.cellPositionHashSet.hashTable);
                }
                this.visibleCellHashTableManager.enable(
                  this.gl, shader, this.visibleCellGpuHashTable);
              }
              if (notCluster) {//gene heatMap
                gl.uniform3fv(shader.uniform('uColorBarRGBList'), new Float32Array(viewer.allDataSource.colorscale.colorData));
                gl.uniform1i(shader.uniform('uColorBarMax'), viewer.color_scale[allToolsButton.genelistFile]?.maxValue ?? 0);
                gl.uniform1i(shader.uniform('uColorBarMin'), viewer.color_scale[allToolsButton.genelistFile]?.minValue ?? 0);
                if (isRect) {
                  gl.uniform2fv(shader.uniform('uPointScaleRange'), new Float32Array([display_size.x, display_size.y])); // x，y pitch的比例，用于绘制矩形，对应矩形宽高比
                } else {
                  gl.uniform2fv(shader.uniform('uPointScaleRange'), new Float32Array([1.0, 1.0])); // x，y pitch的比例，用于绘制矩形，对应矩形宽高比
                }
              } else {
                // 离散颜色
                if (this.segmentStatedColors) {
                  let {gpuSegmentStatedColorHashTable} = this;
                  if (gpuSegmentStatedColorHashTable === undefined ||
                      gpuSegmentStatedColorHashTable.hashTable !== this.segmentStatedColors.hashTable) {
                    gpuSegmentStatedColorHashTable?.dispose();
                    this.gpuSegmentStatedColorHashTable = gpuSegmentStatedColorHashTable =
                        GPUHashTable.get(gl,this.segmentStatedColors.hashTable);
                  }
                  this.segmentStatedColorShaderManager.enable(gl, shader, gpuSegmentStatedColorHashTable);
                  // 默认颜色
                  this.segmentColorShaderManager.enable(gl, shader, this.colorHash || 0);
                }

                // visibleSegment
                if (this.gpuHashTable) {
                  this.hashTableManager.enable(
                    this.gl, shader,this.gpuHashTable);
                }
                gl.uniform1ui(
                  shader.uniform('uShowAllSegments'),
                  this.visibleSegments && this.visibleSegments.hashTable.size ? 0 : 1);
                if (isRect) {
                  gl.uniform2fv(shader.uniform('uPointScaleRange'), new Float32Array([display_size.x, display_size.y])); // x，y pitch的比例，用于绘制矩形，对应矩形宽高比
                } else {
                  gl.uniform2fv(shader.uniform('uPointScaleRange'), new Float32Array([1.0, 1.0])); // x，y pitch的比例，用于绘制矩形，对应矩形宽高比
                }
              }

              initializeLineShader(
                  shader, context.renderContext.projectionParameters, /*featherWidthInPixels=*/ 1);
              drawLines(shader.gl, 1, context.count);
            });
        break;
    }
  }
}

registerAnnotationTypeRenderHandler<Point>(AnnotationType.POINT, {
  sliceViewRenderHelper: RenderHelper,
  perspectiveViewRenderHelper: RenderHelper,
  defineShaderNoOpSetters(builder) {
    builder.addVertexCode(`
void setPointMarkerSize(float size) {}
void setPointMarkerBorderWidth(float size) {}
void setPointMarkerFactor(float size) {}
void setPointMarkerColor(vec4 color) {}
void setPointMarkerOpacityFactor(float a){}
void setPointMarkerBorderColor(vec4 color) {}
`);
  },
  pickIdsPerInstance: 1,
  snapPosition(position, data, offset) {
    position.set(new Float32Array(data, offset, position.length));
  },
  getRepresentativePoint(out, ann) {
    out.set(ann.point);
  },
  updateViaRepresentativePoint(oldAnnotation, position) {
    return {...oldAnnotation, point: new Float32Array(position)};
  }
});
